import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import {
  Brain,
  GitGraph,
  Repeat,
  Plus,
  BookTemplate as Template,
  FileInput as InputIcon,
} from 'lucide-react';
import AgentNode from '../../models/Nodes/AgentNode';
import SemanticMemory from '../../models/Memories/SemanticMemory';
import ProceduralMemory from '../../models/Memories/ProceduralMemory';
import RequiredParametersModal from './RequiredParametersModal';
import { useEnvironment } from '../../context/EnvironmentContext';
import WorkflowService from '../../services/WorkflowService';
import { v4 as uuidv4 } from 'uuid';

interface AddNodeModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (
    agent: AgentNode,
    parameters?: Record<string, string>,
    dependencies?: AgentNode[]
  ) => void;
}

const SelectionButton = React.memo(
  ({
    type,
    icon: Icon,
    title,
    description,
    selected,
    onClick,
  }: {
    type: string;
    icon: React.ElementType;
    title: string;
    description: string;
    selected: boolean;
    onClick: () => void;
  }) => (
    <Paper
      onClick={onClick}
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        border: 2,
        borderColor: selected ? 'pink.500' : 'grey.200',
        bgcolor: selected ? 'pink.50' : 'transparent',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: selected ? 'pink.500' : 'pink.200',
          bgcolor: selected ? 'pink.50' : 'pink.50',
          opacity: 0.5
        }
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={1}>
        <Icon
          sx={{
            width: 32,
            height: 32,
            color: selected ? 'pink.500' : 'grey.400'
          }}
        />
        <Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: selected ? 'pink.500' : 'grey.700'
            }}
          >
            {title}
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
);

const AddNodeModal: React.FC<AddNodeModalProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const { environment } = useEnvironment();
  const [mode, setMode] = useState<'create' | 'template'>('create');
  const [nodeType, setNodeType] = useState<'agent' | 'workflow' | 'loop' | 'input_node' | 'input_agent'>('agent');
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showParamsModal, setShowParamsModal] = useState(false);
  const [requiredParameters, setRequiredParameters] = useState<string[]>([]);
  const [templateAgent, setTemplateAgent] = useState<AgentNode | null>(null);
  const [dependentNodes, setDependentNodes] = useState<AgentNode[]>([]);
  const [selectedAgentDeps, setSelectedAgentDeps] = useState<string[]>([]);
  const [availableAgents, setAvailableAgents] = useState<any[]>([]);

  const handleModeChange = useCallback((newMode: 'create' | 'template') => {
    setMode(newMode);
    setNodeType('agent');
    setSelectedTemplate('');
    setTemplateAgent(null);
    setDependentNodes([]);
    setSelectedAgentDeps([]);
  }, []);

  const handleNodeTypeChange = useCallback((type: 'agent' | 'workflow' | 'loop' | 'input_node' | 'input_agent') => {
    setNodeType(type);
  }, []);

  const handleAgentDepsChange = useCallback((event: any) => {
    setSelectedAgentDeps(event.target.value);
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const workflowService = new WorkflowService(environment);
      const response = await workflowService.listAgents();
      setTemplates(response.data?.agents || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      // TODO: Replace with MUI notification system
      console.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, [environment]);

  const fetchAvailableAgents = useCallback(async () => {
    try {
      const workflowService = new WorkflowService(environment);
      const response = await workflowService.listAgents();
      setAvailableAgents(response.data?.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      // TODO: Replace with MUI notification system
      console.error('Failed to fetch available agents');
    }
  }, [environment]);

  useEffect(() => {
    if (!open) return;
    
    fetchAvailableAgents();
    if (mode === 'template') {
      fetchTemplates();
    }
  }, [open, mode, fetchAvailableAgents, fetchTemplates]);

  const createAgentFromTemplate = useCallback(async (templateData: any, isMainAgent: boolean = true) => {
    const proceduralMemory = ProceduralMemory.fromJson(templateData.procedural_memory_args);
    
    // Ensure we always have a valid ID
    const agentId = isMainAgent 
      ? uuidv4().replace(/-/g, '_') 
      : (templateData._id && typeof templateData._id === 'string' && templateData._id.trim() !== '' 
          ? templateData._id 
          : uuidv4().replace(/-/g, '_'));
    
    const agent = new AgentNode(
      agentId,
      SemanticMemory.fromJson(templateData.semantic_memory_args),
      proceduralMemory,
      'agent_template',
      null,
      null,
      [],
      undefined,
      templateData.required_parameters || [],
      [],
      templateData._id // Pass template ID as agent_template_id
    );
    agent.type = 'agent_template';
    agent.requiredParameters = templateData.required_parameters || [];
    return agent;
  }, []);

  const handleTemplateChange = useCallback(async (value: string) => {
    setSelectedTemplate(value);
    try {
      setLoading(true);
      const workflowService = new WorkflowService(environment);
      const response = await workflowService.getAgentDetails(value);
      const templateData = response.data;
      const agent = await createAgentFromTemplate(templateData);
      setTemplateAgent(agent);
      setRequiredParameters(templateData.required_parameters || []);
      const dependentAgents: AgentNode[] = [];
      if (templateData.depends_on?.length) {
        for (const depId of templateData.depends_on) {
          const depTemplate = templates.find((t) => t._id === depId);
          if (depTemplate) {
            const depAgent = await createAgentFromTemplate(depTemplate, false);
            dependentAgents.push(depAgent);
          }
        }
      }
      setDependentNodes(dependentAgents);
    } catch (error) {
      console.error('Error fetching template details:', error);
      // TODO: Replace with MUI notification system
      console.error('Failed to load template details');
    } finally {
      setLoading(false);
    }
  }, [environment, createAgentFromTemplate, templates]);

  const handleOk = useCallback(async () => {
    if (mode === 'create') {
      const newAgent = new AgentNode(
        uuidv4().replace(/-/g, '_'),
        new SemanticMemory(0.1, []),
        new ProceduralMemory(),
        nodeType
      );
      
      // Ensure the agent has the correct type set
      newAgent.type = nodeType;
      
      const selectedNodes = await Promise.all(
        selectedAgentDeps.map(async (agentId) => {
          const agentData = availableAgents.find((a) => a._id === agentId);
          return agentData ? createAgentFromTemplate(agentData, false) : null;
        })
      );
      onAdd(newAgent, {}, selectedNodes.filter(Boolean) as AgentNode[]);
      onClose();
    } else if (templateAgent) {
      if (requiredParameters.length > 0) {
        setShowParamsModal(true);
      } else {
        const selectedNodes = await Promise.all(
          selectedAgentDeps.map(async (agentId) => {
            const agentData = availableAgents.find((a) => a._id === agentId);
            return agentData ? createAgentFromTemplate(agentData, false) : null;
          })
        );
        const allDeps = [...dependentNodes, ...selectedNodes.filter(Boolean) as AgentNode[]];
        onAdd(templateAgent, {}, allDeps);
        onClose();
      }
    }
  }, [mode, nodeType, selectedAgentDeps, availableAgents, createAgentFromTemplate, onAdd, onClose, templateAgent, requiredParameters.length, dependentNodes]);

  const handleParametersSubmit = useCallback(async (parameters: Record<string, string>) => {
    if (templateAgent) {
      const selectedNodes = await Promise.all(
        selectedAgentDeps.map(async (agentId) => {
          const agentData = availableAgents.find((a) => a._id === agentId);
          return agentData ? createAgentFromTemplate(agentData, false) : null;
        })
      );
      const allDeps = [...dependentNodes, ...selectedNodes.filter(Boolean) as AgentNode[]];
      onAdd(templateAgent, parameters, allDeps);
      setShowParamsModal(false);
      onClose();
    }
  }, [templateAgent, selectedAgentDeps, availableAgents, createAgentFromTemplate, dependentNodes, onAdd, onClose]);

  const handleCancel = useCallback(() => {
    setMode('create');
    setNodeType('agent');
    setSelectedTemplate('');
    setTemplateAgent(null);
    setRequiredParameters([]);
    setDependentNodes([]);
    setSelectedAgentDeps([]);
    onClose();
  }, [onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'grey.800' }}>
            {mode === 'create' ? 'Create New Node' : 'Use Template Node'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'grey.700', mb: 2 }}>
                Choose Creation Mode
              </Typography>
              <Box display="flex" gap={2} sx={{ mb: 3 }}>
                <SelectionButton
                  key="create"
                  type="create"
                  icon={Plus}
                  title="Create New Node"
                  description="Start with a blank node configuration"
                  selected={mode === 'create'}
                  onClick={() => handleModeChange('create')}
                />
                <SelectionButton
                  key="template"
                  type="template"
                  icon={Template}
                  title="Use Template"
                  description="Start from an existing template"
                  selected={mode === 'template'}
                  onClick={() => handleModeChange('template')}
                />
              </Box>
            </Box>

            {mode === 'create' ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'grey.700', mb: 2 }}>
                  Select Node Type
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <Paper
                      onClick={() => handleNodeTypeChange('agent')}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: 2,
                        borderColor: nodeType === 'agent' ? 'blue.500' : 'grey.200',
                        bgcolor: nodeType === 'agent' ? 'blue.50' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: nodeType === 'agent' ? 'blue.500' : 'blue.300',
                          bgcolor: nodeType === 'agent' ? 'blue.50' : 'blue.50',
                          opacity: 0.5
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                                                 <Box
                           sx={{
                             p: 1,
                             borderRadius: 1,
                             bgcolor: nodeType === 'agent' ? 'blue.500' : 'grey.400'
                           }}
                         >
                           <Brain width={20} height={20} color="white" />
                         </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: nodeType === 'agent' ? 'blue.700' : 'grey.700'
                            }}
                          >
                            Standard Agent
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            General purpose agent
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>

                    <Paper
                      onClick={() => handleNodeTypeChange('input_node')}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: 2,
                        borderColor: nodeType === 'input_node' ? 'green.500' : 'grey.200',
                        bgcolor: nodeType === 'input_node' ? 'green.50' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: nodeType === 'input_node' ? 'green.500' : 'green.300',
                          bgcolor: nodeType === 'input_node' ? 'green.50' : 'green.50',
                          opacity: 0.5
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                                                 <Box
                           sx={{
                             p: 1,
                             borderRadius: 1,
                             bgcolor: nodeType === 'input_node' ? 'green.500' : 'grey.400'
                           }}
                         >
                           <InputIcon width={20} height={20} color="white" />
                         </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: nodeType === 'input_node' ? 'green.700' : 'grey.700'
                            }}
                          >
                            Input Node
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Handles workflow input parameters
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>

                  <Box sx={{ borderTop: 1, borderColor: 'grey.200', pt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'grey.600', mb: 1.5 }}>
                      Other Node Types
                    </Typography>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                      <SelectionButton
                        key="workflow"
                        type="workflow"
                        icon={GitGraph}
                        title="Workflow"
                        description="Executes a sub-workflow"
                        selected={nodeType === 'workflow'}
                        onClick={() => handleNodeTypeChange('workflow')}
                      />
                      <SelectionButton
                        key="loop"
                        type="loop"
                        icon={Repeat}
                        title="Loop"
                        description="Iterates over a collection"
                        selected={nodeType === 'loop'}
                        onClick={() => handleNodeTypeChange('loop')}
                      />
                    </Box>
                  </Box>
                </Box>

                <FormControl fullWidth>
                  <InputLabel>Dependencies</InputLabel>
                  <Select
                    multiple
                    value={selectedAgentDeps}
                    onChange={handleAgentDepsChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableAgents.map((agent) => (
                      <MenuItem key={agent._id} value={agent._id}>
                        {agent._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <FormControl fullWidth error={!selectedTemplate}>
                  <InputLabel>Select Template *</InputLabel>
                  {loading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Select
                      value={selectedTemplate}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      label="Select Template *"
                    >
                      {templates.map((template) => (
                        <MenuItem key={template._id} value={template._id}>
                          {template._id}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  {!selectedTemplate && (
                    <Typography variant="caption" color="error">
                      Please select a template
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Dependencies</InputLabel>
                  <Select
                    multiple
                    value={selectedAgentDeps}
                    onChange={handleAgentDepsChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableAgents.map((agent) => (
                      <MenuItem key={agent._id} value={agent._id}>
                        {agent._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button 
            onClick={handleOk} 
            variant="contained"
            disabled={mode === 'template' && !selectedTemplate}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <RequiredParametersModal
        open={showParamsModal}
        onClose={() => setShowParamsModal(false)}
        onSubmit={handleParametersSubmit}
        requiredParameters={requiredParameters}
      />
    </>
  );
};

export default AddNodeModal;